/**
 * crossToast — a cross-platform drop-in replacement for React Native's
 * Android-only `ToastAndroid` module.
 *
 * On Android it simply re-exports the real `ToastAndroid`, so behaviour is
 * unchanged. On iOS/web `ToastAndroid` is `undefined`, and calling
 * `ToastAndroid.showWithGravity(...)` throws "Cannot read property
 * 'showWithGravity' of undefined" — a hard red-screen crash. There this module
 * exposes the same API surface (the `SHORT`/`LONG`/`TOP`/`BOTTOM`/`CENTER`
 * constants and `showWithGravity` / `showWithGravityAndOffset` / `show`) but
 * renders through `react-native-toast-message`, which is already mounted at the
 * app root (`App.jsx`) with the shared `ToastConfig`.
 *
 * Usage — replace `import { ToastAndroid } from 'react-native'` with
 * `import ToastAndroid from '<path>/helpers/crossToast'`; existing call sites
 * (`ToastAndroid.showWithGravity(msg, ToastAndroid.LONG, ToastAndroid.CENTER)`)
 * keep working verbatim on every platform.
 *
 * @module helpers/crossToast
 */
import { Platform, ToastAndroid as RNToastAndroid } from 'react-native';
import Toast from 'react-native-toast-message';

/**
 * Map an Android toast duration to a react-native-toast-message visibilityTime.
 * Android constants are SHORT=0 / LONG=1; anything larger is already a value in
 * milliseconds (call sites pass e.g. 5000 / 15000).
 * @param {number} [duration]
 * @returns {number} visibility time in ms
 */
function resolveVisibilityTime(duration) {
  if (typeof duration !== 'number') return 3000;
  if (duration <= 1) return duration === 1 ? 3500 : 2000; // LONG : SHORT
  return duration;
}

/**
 * Map an Android gravity constant to a toast-message position.
 * @param {number} [gravity]
 * @returns {'top'|'bottom'} position
 */
function resolvePosition(gravity) {
  return gravity === Shim.TOP ? 'top' : 'bottom';
}

const Shim = {
  // ToastAndroid duration + gravity constants (kept numerically distinct).
  SHORT: 0,
  LONG: 1,
  TOP: 2,
  BOTTOM: 3,
  CENTER: 4,

  /**
   * @param {string} message
   * @param {number} [duration]
   */
  show(message, duration) {
    Toast.show({
      type: 'info',
      text1: String(message ?? ''),
      visibilityTime: resolveVisibilityTime(duration),
      position: 'bottom',
    });
  },

  /**
   * @param {string} message
   * @param {number} [duration]
   * @param {number} [gravity]
   */
  showWithGravity(message, duration, gravity) {
    Toast.show({
      type: 'info',
      text1: String(message ?? ''),
      visibilityTime: resolveVisibilityTime(duration),
      position: resolvePosition(gravity),
    });
  },

  /**
   * @param {string} message
   * @param {number} [duration]
   * @param {number} [gravity]
   * @param {number} [_xOffset]
   * @param {number} [_yOffset]
   */
  showWithGravityAndOffset(message, duration, gravity, _xOffset, _yOffset) {
    Toast.show({
      type: 'info',
      text1: String(message ?? ''),
      visibilityTime: resolveVisibilityTime(duration),
      position: resolvePosition(gravity),
    });
  },
};

/**
 * The real `ToastAndroid` on Android; an API-compatible toast-message shim on
 * iOS/web.
 * @type {typeof import('react-native').ToastAndroid}
 */
const CrossToast = Platform.OS === 'android' && RNToastAndroid ? RNToastAndroid : Shim;

export default CrossToast;
